import { getErrorMessage } from '../types';
import { isTokenValid } from '@shared-ui/api';
import { sendOtp, verifyOtp, resetPassword, register as registerAccount } from '@shared-ui/auth';
import { currentTenantPrefix, tenantSlugFromPath, tenantTokenKey, tokenKeyForSlug } from '@shared-ui/tenant';

// Re-export the shared OTP/password helpers so the Login page wires them without
// re-implementing the calls (they hit the app-agnostic /auth/* endpoints).
export { sendOtp, verifyOtp, resetPassword };

/** Unified user-role enum mirroring backend `UserRole`. */
export type UserRole =
  | 'student' | 'teacher'
  | 'tenant_admin' | 'content_admin' | 'user_admin'
  | 'prompt_admin' | 'eval_admin' | 'ops_admin'
  | 'super_admin';

/** The platform scope. A role held here is a *platform role*; a role held at
 *  any other scope is a *tenant role*. See
 *  docs/FeatureDesigns/multi-tenancy/roles-and-scopes.md. */
export const PLATFORM_SCOPE = 'platform';

/** A role held at a scope — the unit of authority. */
export interface RoleAssignment {
  role: UserRole;
  scope: string;
}

export const API_BASE = import.meta.env.PROD
  ? (import.meta.env.VITE_API_BASE || 'https://aitutor-prod-func.azurewebsites.net/api')
  : '/api';

/**
 * Which DEPLOYED environment this build talks to — NOT `import.meta.env.PROD`,
 * which is true of the dev build as well (both are production builds of the
 * app, differing only in which backend they point at). Set per env file.
 *
 * Authoring surfaces that exist to seed content are withheld here: prod content
 * arrives through the dev→prod sync, so a "New …" button in prod creates a row
 * the sync did not put there and will not carry back.
 */
export const IS_PROD_ENV = import.meta.env.VITE_APP_ENV === 'prod';
/** Session key, tenant-scoped on the path form. Exported so main.tsx uses the
 *  same slot rather than a second literal. */
export const TOKEN_KEY = tenantTokenKey('admin_token');
const USER_KEY = 'admin_user';

/**
 * Put this page under the `/t/<slug>/` of the tenant whose record it shows.
 *
 * The console's prefix is wherever the admin signed in, so a record opened from
 * `/t/dps-bandikui/` keeps that prefix whatever tenant the record belongs to —
 * a URL that names the wrong school. This swaps it for the record's own.
 *
 * A full load, because the router's basename is fixed per page load. The token
 * slot is per slug (`tenantTokenKey`), so the current session is carried into
 * the target slot when that one has no live token — otherwise the move would
 * land on the login page. Same origin, same token: what it can read is decided
 * by the token, not by the slot it sits in.
 *
 * No-op when the record has no slug, or the URL already names it.
 */
export function moveToTenantPath(slug: string | undefined): void {
  if (!slug || slug === tenantSlugFromPath()) return;
  const targetKey = tokenKeyForSlug('admin_token', slug);
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !isTokenValid(localStorage.getItem(targetKey))) {
    localStorage.setItem(targetKey, token);
  }
  const { basename } = currentTenantPrefix();
  const { pathname, search, hash } = window.location;
  const rest = basename === '/' ? pathname : pathname.slice(basename.length) || '/';
  window.location.replace(`/t/${slug}${rest}${search}${hash}`);
}
/** The sidebar TenantFilter's selection. Named here so the wipe below can
 *  document it as session state; the filter imports it. */
export const SELECTED_TENANT_KEY = 'admin.selectedTenantId';

/**
 * Keys that survive the session wipe.
 *
 * localStorage is per browser, not per admin, and nothing in this console is
 * keyed by user — so anything left behind by one login is the next login's
 * initial state. The super-admin picked a school in the sidebar; the ops
 * admin who signed in next on the same machine saw only that school's
 * students under a control that read "All tenants". The student and teacher
 * apps wipe on logout and before login for this reason; this list is the
 * only thing the console keeps: how this BROWSER is set up — theme, which
 * sidebar groups are open, hidden table columns, a form's last choices —
 * none of which says anything about whose data is on screen. Session state
 * (token, user, feature gates, experiments, the tenant selection) is
 * everything else, and is wiped by default rather than listed.
 */
const PRESERVED_KEYS = ['padcanvas-theme', 'sidebar-expanded-groups', 'scn-question-panel-open'];
const PRESERVED_PREFIXES = ['dt-cols:', 'remembered:'];

function isPreserved(key: string): boolean {
  return PRESERVED_KEYS.includes(key) || PRESERVED_PREFIXES.some(p => key.startsWith(p));
}

/** Wipe one session's state before the next begins — on logout, and before
 *  a login, since a session can also end by the token expiring. */
export function clearSessionStorage(): void {
  const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
    .filter((k): k is string => k !== null && !isPreserved(k));
  for (const k of keys) localStorage.removeItem(k);
}

export interface AdminUserInfo {
  id: string;
  name: string;
  email: string;
  isSuperAdmin: boolean;
  /** Personas plus (until the legacy cleanup) admin role names. Prefer
   *  `roleAssignments` for authorization — a role name alone says what, never
   *  where. */
  roles: UserRole[];
  /** Scoped authority — drives nav visibility and access checks. */
  roleAssignments?: RoleAssignment[];
  /** Resolved feature gates for this admin. */
  featureGates: Record<string, boolean>;
  /** Tenant the admin's session is scoped to. Super-admin: 'platform' (cross-tenant via TenantFilter). */
  tenantId: string;
}

export interface LoginResponse {
  token: string;
  expiresIn: string;
  user: AdminUserInfo;
}

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    clearSessionStorage();
    const response = await fetch(`${API_BASE}/console/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Login failed');
    }

    // Store token and user info
    localStorage.setItem(TOKEN_KEY, data.data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.data.user));

    return data.data;
  },

  /** Complete an admin invite: set the password via the shared /auth/register
   *  (role 'admin' — valid only for an already-invited admin), then establish
   *  the admin-console session via /console/login. */
  register: async (name: string, email: string, password: string, verificationToken: string): Promise<LoginResponse> => {
    await registerAccount({ name, email, password, verificationToken, role: 'admin' });
    return authApi.login(email, password);
  },

  /** POST /console/google-auth — sign in with a Google ID token. Server
   *  rejects unless the email matches an existing admin user; no register. */
  googleLogin: async (idToken: string): Promise<LoginResponse> => {
    clearSessionStorage();
    const response = await fetch(`${API_BASE}/console/google-auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, clientType: 'web' }),
    });
    const data = await response.json();
    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Google sign-in failed');
    }
    localStorage.setItem(TOKEN_KEY, data.data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
    return data.data;
  },

  logout: () => {
    clearSessionStorage();
  },

  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Merge changes into the cached session user.
   *
   * The console reads the signed-in admin from localStorage rather than from a
   * context, so a name edited on the You page would otherwise keep showing the
   * old value in the sidebar until the next login. The event is what makes the
   * chrome notice — same pattern as `session-expired` in fetchHelper.
   */
  updateUser: (patch: Partial<AdminUserInfo>): AdminUserInfo | null => {
    const current = authApi.getUser();
    if (!current) return null;
    const next = { ...current, ...patch };
    localStorage.setItem(USER_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('admin-user-updated'));
    return next;
  },

  getUser: (): AdminUserInfo | null => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AdminUserInfo;
    } catch {
      return null;
    }
  },

  isSuperAdmin: (): boolean => {
    const user = authApi.getUser();
    return user?.isSuperAdmin === true;
  },

  isAuthenticated: (): boolean => isTokenValid(localStorage.getItem(TOKEN_KEY)),
};
